"use client";

import { useEffect } from "react";
import { LANG_CHANGED, peekLang, restoreLang } from "./LangSwitch";
import { DEFAULT_LANG, isLang } from "./lang";

/**
 * The sticky code panel follows the step you are reading: the last step whose top has passed a line 42% down
 * the window, a line that slides to the window's foot over the page's last stretch, so every step is reached
 * by scrolling however short it is (no step needs a forced height). Changing the language keeps the reader at
 * the same step. On a reference page (`open`) the panel shows the route you last opened instead. A link to
 * something inside a closed disclosure opens it. With no script, every step keeps its code under its words.
 */
export function Follow({ mode = "scroll" }: { mode?: "scroll" | "open" }) {
  useEffect(() => {
    document.documentElement.setAttribute("data-docs", "js");
    const steps = [...document.querySelectorAll<HTMLElement>("[data-frame]")];
    const frames = [...document.querySelectorAll<HTMLElement>(".rail .frame")];
    let current: string | null = null;
    // Only the reader's language's steps are on the page (a line walks each door's sample its own way).
    const shownSteps = () => steps.filter((s) => s.getClientRects().length > 0);
    // A step by its heading, the same in every language's walk (`ts-your-company-s-own-bash`).
    const slugOf = (s: HTMLElement) => {
      const door = s.closest<HTMLElement>(".walk")?.dataset.l;
      return door && s.id.startsWith(`${door}-`)
        ? s.id.slice(door.length + 1)
        : s.id;
    };
    // Where the reader is: the step picked, and where its top sat.
    let at: { slug: string; index: number; top: number } | null = null;
    const pick = () => {
      const shown = shownSteps();
      if (shown.length === 0) return;
      const h = window.innerHeight;
      const left = document.documentElement.scrollHeight - (window.scrollY + h);
      const line = h - Math.min(Math.max(left, 0), h * 0.58);
      let best = shown[0]!;
      for (const s of shown)
        if (s.getBoundingClientRect().top <= line) best = s;
      at = {
        slug: slugOf(best),
        index: shown.indexOf(best),
        top: best.getBoundingClientRect().top,
      };
      const key = best.dataset.frame ?? null;
      if (key === current) return;
      current = key;
      for (const f of frames) f.classList.toggle("on", f.dataset.k === key);
      for (const s of steps) s.classList.toggle("on", s === best);
    };
    // Another language: the same step (by its heading, else its place in the walk) where the old one sat.
    const onLang = () => {
      const was = at;
      const shown = shownSteps();
      if (was && shown.length > 0) {
        const same =
          shown.find((s) => slugOf(s) === was.slug) ??
          shown[Math.min(was.index, shown.length - 1)]!;
        window.scrollBy(0, same.getBoundingClientRect().top - was.top);
      }
      pick();
    };
    let queued = 0;
    const onScroll = () => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        pick();
      });
    };
    // A reference page: the panel shows the route you last opened.
    const show = (key: string) => {
      for (const f of frames) f.classList.toggle("on", f.dataset.k === key);
    };
    let peeked = false;
    const openTarget = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      // A link to another language's entry (a Python method, a CLI command, from the search) shows that
      // language on this page only: the top bar's choice is the one that is kept.
      const door = target.closest<HTMLElement>(".lc[data-l]")?.dataset.l;
      const at =
        document.documentElement.getAttribute("data-lang") ?? DEFAULT_LANG;
      let switched = false;
      if (isLang(door) && door !== at) {
        peekLang(door);
        peeked = true;
        switched = true;
      }
      // Named again while it is open, a route still takes the panel.
      const key = target.closest<HTMLElement>("[data-frame]")?.dataset.frame;
      if (mode === "open" && key) show(key);
      // A field deep in a route opens every disclosure around it, the route and its group included.
      let closed = target.closest("details:not([open])");
      const opened = closed !== null;
      while (closed instanceof HTMLDetailsElement) {
        closed.open = true;
        closed = closed.parentElement?.closest("details:not([open])") ?? null;
      }
      if (switched || opened) target.scrollIntoView();
    };
    const onToggle = (e: Event) => {
      const d = e.target;
      if (d instanceof HTMLDetailsElement && d.open && d.dataset.frame)
        show(d.dataset.frame);
    };
    openTarget();
    if (mode === "open") {
      // toggle does not bubble: listen in the capture phase, for every route at once.
      document.addEventListener("toggle", onToggle, true);
      window.addEventListener("hashchange", openTarget);
      return () => {
        document.removeEventListener("toggle", onToggle, true);
        window.removeEventListener("hashchange", openTarget);
        // Leaving the page, the language a link showed gives way to the reader's own.
        if (peeked) restoreLang();
      };
    }
    pick();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", pick);
    window.addEventListener("hashchange", openTarget);
    window.addEventListener(LANG_CHANGED, onLang);
    return () => {
      cancelAnimationFrame(queued);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", pick);
      window.removeEventListener("hashchange", openTarget);
      window.removeEventListener(LANG_CHANGED, onLang);
      if (peeked) restoreLang();
    };
  }, [mode]);
  return null;
}
